import { API_BASE, getAuthHeaders, apiFetch } from './api-fetch';
import { StockPost } from '../components/StockSharingFeed';
import { authService } from './auth';

// Check if error is a server down error
function isServerDownError(error: any): boolean {
  return error && (
    error.message?.includes('521') ||
    error.message?.includes('Web server is down') ||
    error.toString().includes('<!DOCTYPE html>')
  );
}

// Normalise a raw backend message object into the StockPost message shape.
// Content may be plain text OR JSON-encoded { text, imageUrl } (used when sharing images).
function normalizeMessage(msg: any): StockPost['messages'][0] {
  let text = '';
  let image: string | undefined = msg.image || undefined;

  const raw = msg.text ?? msg.content ?? '';
  if (typeof raw === 'string' && raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      text = parsed.text ?? parsed.t ?? '';
      image = image ?? parsed.imageUrl ?? parsed.i ?? undefined;
    } catch {
      text = raw;
    }
  } else {
    text = String(raw);
  }

  return {
    id: msg.id ?? msg.messageId ?? String(Date.now() + Math.random()),
    userId: msg.userId ?? msg.authorId ?? '',
    userName: msg.userName ?? msg.authorName ?? msg.author ?? msg.senderName ?? '',
    userRole: msg.userRole ?? msg.role ?? '',
    text,
    image,
    timestamp: new Date(msg.timestamp ?? msg.createdAt ?? Date.now()),
    isSystem: msg.isSystem ?? false,
    isInterest: msg.isInterest ?? false,
  };
}

export const stockAPI = {
  // Fetch all stock posts
  async getPosts(): Promise<StockPost[]> {
    try {
      const response = await apiFetch(`${API_BASE}/stock-posts`, {
        headers: getAuthHeaders(),
        signal: AbortSignal.timeout(10000)
      });

      if (!response.ok) {
        // 401/403: auth failure — throw so caller can stop polling
        if (response.status === 401 || response.status === 403) {
          throw Object.assign(new Error('Stock feed unavailable — auth required'), { status: response.status });
        }
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('text/html')) return [];
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (!error.error?.includes('521')) console.error('Error fetching stock posts:', error);
        return [];
      }

      const data = await response.json();

      // Spec envelope: { success, data: { posts: [] } } or { data: { items: [] } }
      const rawPosts = data?.data?.posts ?? data?.data?.items;
      if (!rawPosts || !Array.isArray(rawPosts)) return [];

      return rawPosts.map((post: any) => {
        let parsed: any = {};
        try { if (post.content) parsed = JSON.parse(post.content); } catch {}
        return {
          ...post,
          ...parsed,
          id: post.id ?? post.postId,
          authorId: post.authorId ?? '',
          propertyTitle: parsed.propertyTitle || post.title || '',
          agentName: (() => {
            const n = parsed.agentName || post.agentName || post.author || '';
            if (!n || n === 'Unknown Agent') {
              const a = post.author || '';
              return a.includes('@') ? a.split('@')[0] : (a || 'Agent');
            }
            return n;
          })(),
          timestamp: new Date(post.timestamp ?? post.createdAt),
          status: post.status === 'published' ? 'ACTIVE'
                : post.status === 'draft' ? 'ACTIVE'
                : post.status || 'ACTIVE',
          messages: (post.messages || []).map(normalizeMessage)
        };
      });
    } catch (error: any) {
      if (!isServerDownError(error)) console.error('Error in getPosts:', error);
      return [];
    }
  },

  // Fetch messages for a single post (used to refresh chat on open)
  async getMessages(postId: string): Promise<StockPost['messages']> {
    try {
      const response = await apiFetch(`${API_BASE}/stock-posts/${postId}/messages`, {
        headers: getAuthHeaders(),
        signal: AbortSignal.timeout(10000)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error fetching messages:', error);
        return [];
      }

      const data = await response.json();
      // Backend returns SuccessResponse — messages may be at data.messages, data.data.messages, or data.data (array)
      const raw = data?.data?.messages ?? (Array.isArray(data?.data) ? data.data : null) ?? data?.messages;
      if (!Array.isArray(raw)) return [];
      return raw.map(normalizeMessage);
    } catch (error) {
      console.error('Error in getMessages:', error);
      return [];
    }
  },

  // Create a new stock post
  async createPost(postData: Omit<StockPost, 'id' | 'timestamp' | 'status' | 'messages'>): Promise<StockPost> {
    try {
      const currentUser = authService.getCurrentUser();
      const payload = {
        title: `[${postData.type}] ${postData.propertyTitle}`,
        content: JSON.stringify({
          type: postData.type,
          propertyTitle: postData.propertyTitle,
          propertyType: postData.propertyType,
          location: postData.location,
          price: postData.price,
          bedrooms: postData.bedrooms,
          bathrooms: postData.bathrooms,
          size: postData.size,
          description: postData.description,
          agentName: postData.agentName,
          agency: postData.agency,
          source: postData.source,
          internalPropertyId: postData.internalPropertyId,
          externalListingUrl: postData.externalListingUrl,
        }),
        author: postData.agentName,
        authorId: currentUser?.id || '',
      };

      const response = await apiFetch(`${API_BASE}/stock-posts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error creating stock post:', error);
        throw new Error(error.error || 'Failed to create post');
      }

      const data = await response.json();
      const post = data?.data?.post ?? data?.data ?? data?.post;
      return {
        ...postData,
        id: post?.postId ?? post?.id ?? '',
        authorId: post?.authorId ?? currentUser?.id ?? '',
        timestamp: new Date(post?.createdAt ?? Date.now()),
        status: 'ACTIVE',
        messages: [],
      };
    } catch (error) {
      console.error('Error in createPost:', error);
      throw error;
    }
  },

  // Update post status
  async updateStatus(postId: string, status: StockPost['status']): Promise<StockPost> {
    try {
      const response = await apiFetch(`${API_BASE}/stock-posts/${postId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Error updating post status:', error);
        throw new Error(error.error || 'Failed to update status');
      }

      const data = await response.json();
      const updatedPost = data?.data?.post ?? data?.data ?? data?.post;

      // Backend confirmed the update. Return full post if available, otherwise minimal shape.
      // Caller (StockSharingFeed) merges this with local state.
      if (!updatedPost?.postId && !updatedPost?.id) {
        return { id: postId, status } as StockPost;
      }
      return {
        ...updatedPost,
        id: updatedPost.postId ?? updatedPost.id ?? postId,
        timestamp: new Date(updatedPost.timestamp ?? Date.now()),
        messages: (updatedPost.messages || []).map(normalizeMessage)
      };
    } catch (error) {
      console.error('Error in updateStatus:', error);
      throw error;
    }
  },

  // Add message to post
  async addMessage(
    postId: string,
    userId: string,
    userName: string,
    userRole: string,
    text: string,
    isInterest: boolean = false,
    imageUrl?: string | null
  ): Promise<{ message: StockPost['messages'][0]; post: StockPost }> {
    try {
      // Encode image URL inside content string when present (backend only accepts { content: string })
      const content = imageUrl
        ? JSON.stringify({ text, imageUrl })
        : text;

      const response = await apiFetch(`${API_BASE}/stock-posts/${postId}/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Error adding message:', error);
        throw new Error(error.error || 'Failed to add message');
      }

      const data = await response.json();
      const rawMessage = data?.data?.message ?? data?.data ?? null;
      const rawPost = data?.data?.post ?? null;

      // Normalise the message — if backend returns it, use that; otherwise build optimistic local message.
      // Backend currently returns authorId:"" and authorName:"" (does not populate from JWT),
      // so we override with the passed userId/userName/userRole so the message displays correctly.
      const message = normalizeMessage(
        rawMessage?.id || rawMessage?.messageId
          ? {
              ...rawMessage,
              userId: rawMessage.authorId || userId,
              userName: rawMessage.authorName || rawMessage.userName || userName,
              userRole: rawMessage.userRole || userRole,
              image: rawMessage.image ?? (imageUrl || undefined),
            }
          : {
              id: `local-${Date.now()}`,
              userId,
              userName,
              userRole,
              content,
              image: imageUrl || undefined,
              createdAt: Date.now(),
              isSystem: isInterest,
              isInterest,
            }
      );

      // Return partial post shape if backend didn't return full post (caller merges with local state)
      const post: StockPost = rawPost?.id || rawPost?.postId
        ? {
            ...rawPost,
            id: rawPost.postId ?? rawPost.id,
            timestamp: new Date(rawPost.timestamp ?? Date.now()),
            messages: (rawPost.messages || []).map(normalizeMessage)
          }
        : { id: '' } as StockPost;

      return { message, post };
    } catch (error) {
      console.error('Error in addMessage:', error);
      throw error;
    }
  },

  // Delete a post
  async deletePost(postId: string): Promise<void> {
    try {
      const response = await apiFetch(`${API_BASE}/stock-posts/${postId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Error deleting post:', error);
        throw new Error(error.error || 'Failed to delete post');
      }
    } catch (error) {
      console.error('Error in deletePost:', error);
      throw error;
    }
  },

  // Fix old posts with incorrect status (migration utility)
  async fixPostsStatus(): Promise<{ fixedCount: number }> {
    try {
      const response = await apiFetch(`${API_BASE}/stock-posts/fix-status`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Error fixing posts:', error);
        throw new Error(error.error || 'Failed to fix posts');
      }

      const data = await response.json();
      return { fixedCount: data.fixedCount };
    } catch (error) {
      console.error('Error in fixPostsStatus:', error);
      throw error;
    }
  },
};
